import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, LogIn, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import api from '@/services/api';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { useTranslation } from 'react-i18next';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth, user, isAuthenticated, _hasHydrated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { t } = useTranslation(['auth', 'common']);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Redirect if already logged in
  useEffect(() => {
    // Only redirect after hydration is complete
    if (_hasHydrated && isAuthenticated && user) {
      const redirectPath = user.role === 'CANDIDATE' ? '/candidate/dashboard' : '/recruiter/dashboard'
      navigate(redirectPath, { replace: true })
    }
  }, [_hasHydrated, isAuthenticated, user, navigate])

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError(null);

    try {
      const response = await api.post('/auth/login', data);
      const { user, accessToken, refreshToken } = response.data;

      setAuth(user, accessToken, refreshToken);

      toast.success('Welcome back!');

      const redirectPath = user.role === 'CANDIDATE'
        ? '/candidate/dashboard'
        : user.role === 'RECRUITER'
        ? '/recruiter/dashboard'
        : '/';

      navigate(redirectPath, { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { code?: string; message?: string; remainingMinutes?: number } | string } } };

      // Handle account locked error specifically
      if (err.response?.data?.error && typeof err.response.data.error === 'object' && err.response.data.error.code === 'ACCOUNT_LOCKED') {
        const errorData = err.response.data.error as { message?: string; remainingMinutes?: number };
        const lockMessage = errorData.remainingMinutes
          ? `${errorData.message}. Please try again in ${errorData.remainingMinutes} minute${errorData.remainingMinutes !== 1 ? 's' : ''}.`
          : errorData.message || 'Account locked';
        setLoginError(lockMessage);
      } else {
        const errorMessage = typeof err.response?.data?.error === 'object'
          ? err.response.data.error.message
          : err.response?.data?.error
          || 'Login failed. Please try again.';
        setLoginError(errorMessage as string);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-purple-50/60 fixed inset-0 overflow-auto">
      {/* Glow blobs */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-50" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-40" />
      </div>
      <div className="relative w-full max-w-md my-auto">
        {/* Back to Home Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">{t('common:nav.home')}</span>
        </Link>

        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {t('auth:login.title')}
          </h1>
          <p className="text-gray-600">{t('auth:login.subtitle')}</p>
        </div>

        {/* Login Form */}
        <div className="bg-white/70 backdrop-blur-sm p-8 rounded-2xl border border-gray-200 shadow-lg">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              {...register('email')}
              type="email"
              label={t('auth:login.emailLabel')}
              placeholder={t('auth:login.emailPlaceholder')}
              error={errors.email?.message}
              icon={<Mail className="w-5 h-5" />}
              autoComplete="email"
              disabled={isLoading}
            />

            <Input
              {...register('password')}
              type="password"
              label={t('auth:login.passwordLabel')}
              placeholder={t('auth:login.passwordPlaceholder')}
              error={errors.password?.message}
              icon={<Lock className="w-5 h-5" />}
              autoComplete="current-password"
              disabled={isLoading}
            />

            <div className="flex justify-end text-sm">
              <Link
                to="/forgot-password"
                className="text-blue-600 hover:text-blue-700 transition-colors"
              >
                {t('auth:login.forgotPassword')}
              </Link>
            </div>

            {loginError && (
              <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{loginError}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              isLoading={isLoading}
              className="group"
            >
              {!isLoading && <LogIn className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />}
              {t('auth:login.submitButton')}
            </Button>
          </form>

          <div className="my-6 border-t border-gray-200"></div>

          <p className="text-center text-gray-600">
            {t('auth:login.noAccount')}{' '}
            <Link
              to="/register"
              className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
            >
              {t('auth:login.signUpLink')}
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
}
